import { create } from 'zustand';

import { notificationRepository } from 'src/repositories/notifications/notification.repository';
import { NotificationCampaign } from 'src/types/notification';

export const TIMER_DURATION = 10; // 10 segundos
const MAX_PARALLEL_SENDS = 2; // Máximo de envios em paralelo

type TimerItem = {
  campaignId: string;
  campaignData: NotificationCampaign;
  remainingTime: number;
  timerId: NodeJS.Timeout;
};

type TimerState = {
  activeTimers: TimerItem[];
};

type TimerActions = {
  startSendTimer: (campaignId: string, campaignData: NotificationCampaign) => void;
  cancelNotification: (campaignId: string) => Promise<void>;
  clearTimer: (campaignId: string) => void;
  clearAllTimers: () => void;
  canSendMore: () => boolean;
  getActiveCount: () => number;
  isCampaignSending: (campaignId: string) => boolean;
};

export const useNotificationSendTimerStore = create<TimerState & TimerActions>((set, get) => ({
  activeTimers: [],

  canSendMore: () => get().activeTimers.length < MAX_PARALLEL_SENDS,

  getActiveCount: () => get().activeTimers.length,

  isCampaignSending: (campaignId: string) => get().activeTimers.some((t) => t.campaignId === campaignId),

  startSendTimer: (campaignId: string, campaignData: NotificationCampaign) => {
    const { activeTimers, canSendMore } = get();

    // Verifica se já atingiu o limite
    if (!canSendMore()) {
      console.warn('Limite máximo de envios em paralelo atingido');
      return;
    }

    // Verifica se já existe um timer para esta campanha
    const existingTimer = activeTimers.find((t) => t.campaignId === campaignId);
    if (existingTimer) {
      return;
    }

    // Inicia o novo timer
    const newTimerId = setInterval(() => {
      const state = get();
      const timerIndex = state.activeTimers.findIndex((t) => t.campaignId === campaignId);

      if (timerIndex === -1) return;

      const timer = state.activeTimers[timerIndex];

      if (timer.remainingTime <= 1) {
        // Timer terminou - remove da lista
        clearInterval(timer.timerId);
        set({
          activeTimers: state.activeTimers.filter((t) => t.campaignId !== campaignId),
        });
      } else {
        // Decrementa o tempo
        const updatedTimers = [...state.activeTimers];
        updatedTimers[timerIndex] = {
          ...timer,
          remainingTime: timer.remainingTime - 1,
        };
        set({ activeTimers: updatedTimers });
      }
    }, 1000);

    const newTimer: TimerItem = {
      campaignId,
      campaignData,
      remainingTime: TIMER_DURATION,
      timerId: newTimerId,
    };

    set({
      activeTimers: [...activeTimers, newTimer],
    });
  },

  cancelNotification: async (campaignId: string) => {
    const { activeTimers } = get();
    const timer = activeTimers.find((t) => t.campaignId === campaignId);

    if (!timer) return;

    // Para o timer
    clearInterval(timer.timerId);

    // Atualiza o status da campanha para 'pending' enviando o objeto completo
    await notificationRepository.updateCampaign(campaignId, {
      title: timer.campaignData.title,
      body: timer.campaignData.body,
      type: timer.campaignData.type,
      targetId: timer.campaignData.targetId,
      imageUrl: timer.campaignData.imageUrl,
      segmentIds: timer.campaignData.segmentIds,
      targetAll: timer.campaignData.targetAll,
      scheduledAt: timer.campaignData.scheduledAt,
      status: 'pending',
    });

    // Remove o timer da lista
    set({
      activeTimers: activeTimers.filter((t) => t.campaignId !== campaignId),
    });
  },

  clearTimer: (campaignId: string) => {
    const { activeTimers } = get();
    const timer = activeTimers.find((t) => t.campaignId === campaignId);

    if (timer) {
      clearInterval(timer.timerId);
      set({
        activeTimers: activeTimers.filter((t) => t.campaignId !== campaignId),
      });
    }
  },

  clearAllTimers: () => {
    const { activeTimers } = get();
    activeTimers.forEach((timer) => clearInterval(timer.timerId));
    set({ activeTimers: [] });
  },
}));
